import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { HERO_PHOTO } from "../lib/assets";
import { useReservationModal } from "../contexts/ReservationModalContext";

export default function Hero({ settings }) {
  const navigate = useNavigate();
  const { open: openReservation } = useReservationModal();
  const headline = settings?.hero_headline || "DC'S PREMIER ROOFTOP NIGHTLIFE EXPERIENCE";
  const sub = settings?.hero_subheadline || "Luxury rooftop lounge, premium cocktails, curated nightlife, VIP tables, and unforgettable events in Washington DC.";
  const heroImg = settings?.hero_poster_url || HERO_PHOTO;
  const heroVideo = settings?.hero_video_url;

  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onReady = () => setVideoReady(true);
    v.addEventListener("playing", onReady);
    v.addEventListener("loadeddata", onReady);
    return () => {
      v.removeEventListener("playing", onReady);
      v.removeEventListener("loadeddata", onReady);
    };
  }, [heroVideo]);

  return (
    <section data-testid="hero-section" className="relative h-[85vh] min-h-[560px] max-h-[820px] w-full overflow-hidden bg-black">
      <div className="absolute inset-0 z-0 bg-black overflow-hidden">
        {heroVideo ? (
          <video
            ref={videoRef}
            autoPlay loop muted playsInline preload="auto"
            poster="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'><rect width='1' height='1' fill='%23000'/></svg>"
            className="w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: videoReady ? 1 : 0 }}
            src={heroVideo}
          />
        ) : (
          <img src={heroImg} alt="Onyx rooftop" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/80" />
      </div>

      <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-center pt-20 pb-12">
        <div className="max-w-4xl fade-up">
          <div className="flex items-center gap-3 mb-5 fade-up delay-100">
            <span className="block w-10 h-px bg-[var(--onyx-gold)]" />
            <span className="eyebrow">Washington · D.C. · Dupont After Dark</span>
          </div>
          <h1
            data-testid="hero-headline"
            className="font-display text-white text-4xl md:text-6xl lg:text-[5.5rem] leading-[1] mb-6 fade-up delay-200"
            style={{ fontWeight: 300, letterSpacing: "-0.025em" }}
          >
            {headline}
          </h1>
          <p
            data-testid="hero-subheadline"
            className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed mb-8 fade-up delay-300"
          >
            {sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 fade-up delay-500">
            <button
              data-testid="hero-cta-reserve"
              onClick={() => openReservation()}
              className="btn-luxe btn-luxe-gold"
            >
              Reserve Table
            </button>
            <button
              data-testid="hero-cta-events"
              onClick={() => navigate("/events")}
              className="btn-luxe"
              style={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff" }}
            >
              Upcoming Events
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40 flex flex-col items-center gap-1.5 animate-bounce">
        <span className="text-[10px] tracking-luxe uppercase">Scroll</span>
        <ChevronDown size={14} />
      </div>
    </section>
  );
}
